/**
 * Tool → required capability map (frontend mirror).
 *
 * MIRRORS `backend/app/services/crucible_tools.py::get_tool_capability_requirements`
 * (and its `_TOOL_CAP_PREFIXES` / `_TOOL_CAP_EXACT` tables). Parity is pinned by
 * `shared/tool-capability-map.json`: the backend test
 * `tests/unit/rbac/test_tool_capability_map_fixture.py` regenerates/checks it and
 * `tests/lib/rbac/tool-capabilities-parity.test.ts` checks this file against it.
 *
 * This is a UX-only convenience used to suppress tools the user lacks the
 * capability for BEFORE
 * the model sees them. The BACKEND tool-dispatch guard (Task 8) remains the
 * authoritative enforcement point.
 *
 * ⚠️ KEEP IN SYNC with the backend. Convention: a tool requires its OWNING
 * SURFACE capability. Any tool NOT matched here has NO required capability and
 * is default-allow — never cap-suppressed.
 */

import {
  EDC_TOOLS_CAP,
  INTERNAL_CONTEXT_CAP,
  OPEN_PAYMENTS_TOOLS_CAP,
  QMS_EVENTS_TRAINING,
  STRIDE_TOOLS_CAP,
} from './roles'

/** Prefix → required surface capability. Order-independent (prefixes disjoint). */
const TOOL_CAP_PREFIXES: ReadonlyArray<readonly [string, string]> = [
  // Marq / BD CRM tooling
  ['hubspot_', 'surface:marq'],
  // Qualio general document/tag/template lookup — org-wide via tool:qualio_*
  // (see roles.ts "member"). Sensitive tools are exact-overridden below:
  // events/change controls/training behind tool:qms_events_training (or
  // surface:audrey); suppliers, risk, user detail, audit trail and
  // Audrey-rendered diagrams behind surface:audrey only.
  ['qualio_', 'tool:qualio_*'],
  // Nora codebook tooling
  ['nora_', 'surface:nora'],
  // EDC mirror tooling — tool:edc is held by no role, so only a platform
  // admin ('*') passes (the backend STUDY_EDC_TOOL_ALLOWLIST still applies).
  ['edc_', EDC_TOOLS_CAP],
  // STRIDE study library + MDD — internal role only. A prefix so a new
  // stride_* tool is gated from day one. Mirrors backend _TOOL_CAP_PREFIXES.
  ['stride_', STRIDE_TOOLS_CAP],
]

/**
 * Tools that require ANY of the listed caps (Living Codebook grants).
 * Checked before TOOL_CAP_EXACT in toolRequiredCapabilities.
 * Mirrors backend CODEBOOK_LIST_CAPS / CODEBOOK_SHARED_READ_CAPS.
 */
const TOOL_CAP_ANY: Readonly<Record<string, readonly string[]>> = {
  // 3PH staff reach the shared codebook reads from Concierge through
  // context:3ph_internal — surface:concierge used to stand in for that, which
  // handed 3PH codebooks to every external partner account.
  codebook_list: ['surface:norma', INTERNAL_CONTEXT_CAP, 'surface:study-design'],
  codebook_get: [
    INTERNAL_CONTEXT_CAP,
    'surface:val',
    'surface:norma',
    'surface:testcases',
    'surface:study-design',
  ],
  codebook_search: [
    INTERNAL_CONTEXT_CAP,
    'surface:val',
    'surface:norma',
    'surface:testcases',
    'surface:study-design',
  ],
  // P1/P2 Norma-only deep tools (any-of with a single cap — mirrors backend).
  codebook_form_get: ['surface:norma'],
  codebook_diff: ['surface:norma'],
  codebook_structure_get: ['surface:norma'],
  codebook_field_search: ['surface:norma'],
  codebook_field_get: ['surface:norma'],
  codebook_inventory: ['surface:norma'],
  codebook_rules_scan: ['surface:norma'],
  codebook_integrity_check: ['surface:norma'],
  // Qualio events, change controls and training records — every Compli tier
  // (tool:qms_events_training) or Audrey. Mirrors backend
  // QUALIO_EVENTS_TRAINING_CAPS.
  qualio_list_events: [QMS_EVENTS_TRAINING, 'surface:audrey'],
  qualio_get_event: [QMS_EVENTS_TRAINING, 'surface:audrey'],
  qualio_list_event_templates: [QMS_EVENTS_TRAINING, 'surface:audrey'],
  qualio_get_event_template: [QMS_EVENTS_TRAINING, 'surface:audrey'],
  qualio_list_change_controls: [QMS_EVENTS_TRAINING, 'surface:audrey'],
  qualio_list_user_training: [QMS_EVENTS_TRAINING, 'surface:audrey'],
}

/** Exact tool name → required surface capability (no distinguishing prefix). */
const TOOL_CAP_EXACT: Readonly<Record<string, string>> = {
  // Living Codebook library (P0) — primary cap; full any-of in TOOL_CAP_ANY.
  codebook_list: 'surface:norma',
  codebook_get: 'surface:norma',
  codebook_search: 'surface:norma',
  codebook_form_get: 'surface:norma',
  codebook_diff: 'surface:norma',
  codebook_structure_get: 'surface:norma',
  codebook_field_search: 'surface:norma',
  codebook_field_get: 'surface:norma',
  codebook_inventory: 'surface:norma',
  codebook_rules_scan: 'surface:norma',
  codebook_integrity_check: 'surface:norma',
  // Qualio events/training — primary cap; full any-of in TOOL_CAP_ANY.
  qualio_list_events: QMS_EVENTS_TRAINING,
  qualio_get_event: QMS_EVENTS_TRAINING,
  qualio_list_event_templates: QMS_EVENTS_TRAINING,
  qualio_get_event_template: QMS_EVENTS_TRAINING,
  qualio_list_change_controls: QMS_EVENTS_TRAINING,
  qualio_list_user_training: QMS_EVENTS_TRAINING,
  // Qualio tools that stay Audrey-only — override the qualio_ prefix default
  // (tool:qualio_*). Mirrors backend _TOOL_CAP_EXACT exactly.
  qualio_list_suppliers: 'surface:audrey',
  qualio_get_supplier: 'surface:audrey',
  qualio_list_supplier_audits: 'surface:audrey',
  qualio_list_risk_levels: 'surface:audrey',
  qualio_get_risk_settings: 'surface:audrey',
  qualio_get_user: 'surface:audrey',
  qualio_get_user_assignments: 'surface:audrey',
  qualio_list_audit_trail: 'surface:audrey',
  qualio_consistency_check: 'surface:audrey',
  qualio_process_map: 'surface:audrey',
  qualio_cross_references: 'surface:audrey',
  // ODM XML / study-design authoring + display (UI tools)
  // add_to_canvas is deliberately NOT here — it's persona-agnostic (every
  // hasChatMode workspace writes to its OWN canvas; see registry.ts
  // toolGrants). Gating it to surface:study-design silently stripped it
  // from every Concierge/Marq/Norma/Val/TestCases user's tool list even
  // though none of those modes' suppressedTools exclude it — the model
  // would then correctly report "I don't have a canvas tool" when asked
  // to save/send content there.
  update_odm_xml: 'surface:study-design',
  // Writes the ODM study shell — had no mapping (default-allow) until 2026-11.
  generate_study_shell: 'surface:study-design',
  show_form_preview: 'surface:study-design',
  update_study_config: 'surface:study-design',
  display_study: 'surface:study-design',
  compare_odm: 'surface:study-design',
  // ODM file inspection tools (study-design)
  get_form: 'surface:study-design',
  get_item: 'surface:study-design',
  get_itemgroup: 'surface:study-design',
  get_codelist: 'surface:study-design',
  search_odm: 'surface:study-design',
  // Parallel-build / ODM-validation internals — were default-allow (no
  // capability check at all) for every Concierge user; these are
  // study-design authoring internals and belong behind the same surface
  // as the rest of the study-design toolset (2026-07-29).
  validate_3ph_odm: 'surface:study-design',
  get_active_process_status: 'surface:study-design',
  override_build_plan: 'surface:study-design',
  present_build_plan: 'surface:study-design',
  parse_crf_spec: 'surface:study-design',
  // CMS Open Payments lookups — public CMS data, held by bd and external
  // (2026-11). Was surface:marq (2026-07-29), before that default-allow.
  provider_payment_lookup: OPEN_PAYMENTS_TOOLS_CAP,
  payer_funding_summary: OPEN_PAYMENTS_TOOLS_CAP,
  // SurveyMonkey authoring (member+ only — not PoTM read-only cohort)
  survey_publish: 'tool:survey_write',
  survey_extend: 'tool:survey_write',
  survey_modify_question: 'tool:survey_write',
}

/**
 * Concrete, enumerable tool names that carry a required capability.
 *
 * Only the EXACT-mapped tools are enumerable here — prefix-mapped families
 * (hubspot_*, qualio_*, …) are open-ended and resolved against the live tool
 * list at the call site. `effectiveSuppressedTools` unions this base set with
 * the mode's static suppression set (which already lists the concrete hubspot
 * tool names) plus any caller-supplied candidate names.
 */
export const CAP_MAPPED_TOOL_NAMES: readonly string[] = Object.keys(TOOL_CAP_EXACT)

/**
 * Capabilities of which the user must hold ANY one, or `null` (default-allow).
 * Prefer this over {@link toolRequiredCapability} for shared-read tools.
 */
export function toolRequiredCapabilities(toolName: string): string[] | null {
  if (toolName in TOOL_CAP_ANY) return [...TOOL_CAP_ANY[toolName]]
  const single = toolRequiredCapability(toolName)
  return single ? [single] : null
}

/**
 * Return the primary capability required to use `toolName`, or `null` when the
 * tool is unmapped. Callers MUST treat `null` as default-allow (never suppress).
 * For any-of tools, returns the first listed cap — use
 * {@link toolRequiredCapabilities} when checking access.
 */
export function toolRequiredCapability(toolName: string): string | null {
  if (toolName in TOOL_CAP_EXACT) return TOOL_CAP_EXACT[toolName]
  if (toolName.startsWith('survey_')) return 'tool:survey_read'
  for (const [prefix, cap] of TOOL_CAP_PREFIXES) {
    if (toolName.startsWith(prefix)) return cap
  }
  return null
}
